import React, { useState, createContext, useContext, memo } from 'react';
type Language = 'en' | 'id';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const translations = {
  en: {
    'nav.about': 'About',
    'nav.philosophy': 'Philosophy',
    'nav.solutions': 'Solutions',
    'nav.technology': 'Technology',
    'nav.ethics': 'Ethics',
    'nav.insights': 'NETKRIDA',
    'nav.contact': 'Contact',
    'home.hero.title': 'TECHNOLOGY BEYOND CONTROL.',
    'home.hero.subtitle': 'We design future technology systems that are intelligent, autonomous, and ethical by design.',
    'home.hero.cta1': 'Explore Solutions',
    'home.hero.cta2': 'Initiate Protocol',
    'home.stats.projects': 'Years Experience',
    'home.stats.partners': 'Global Partners',
    'home.stats.sovereignty': 'Data Sovereignty %',
    'home.stats.monitoring': 'Hour Monitoring',
    'home.manifesto.title': 'Institutional Manifesto',
    'home.manifesto.heading': 'This is not a company from today. This is a company from the next decade.',
    'home.manifesto.desc': 'NEW ASIA TECH represents a new civilizational approach to advanced technology. We operate at the intersection of artificial intelligence, cyber-physical systems, and future infrastructure. Our technology is advanced, artificial, and computational — yet consciously bounded.',
    'home.manifesto.cta': 'Read Our Philosophy',
    'home.domains.title': 'CORE DOMAINS',
    'home.domains.subtitle': 'STRATEGIC CAPABILITIES',
    'home.domains.ai.title': 'Artificial Intelligence',
    'home.domains.ai.desc': 'Large-scale decision support systems and autonomous agents for complex environments.',
    'home.domains.cyber.title': 'Cyber-Physical Systems',
    'home.domains.cyber.desc': 'Bridging the digital and physical worlds through advanced IoT and control infrastructure.',
    'home.domains.defense.title': 'Sovereign Defense',
    'home.domains.defense.desc': 'Human-in-the-loop security systems designed for national and institutional resilience.',
    'home.cta.title': 'BUILDING THE NEXT ERA',
    'home.cta.desc': 'Join the vanguard of sovereign technology. Secure your infrastructure for the century ahead.',
    'home.cta.button': 'Initiate Contact',
    'about.title': 'INSTITUTIONAL PROFILE',
    'about.who': 'Who We Are',
    'about.who.desc': 'NEW ASIA TECH is a strategic technology institution focused on the design and implementation of sovereign intelligence systems.',
    'about.paradigm': 'Post-Startup Paradigm',
    'about.paradigm.desc': 'We reject the "move fast and break things" ideology of the early 21st century. In an era of critical artificial intelligence and autonomous infrastructure, stability, precision, and ethical governance are paramount. We do not build MVPs; we build institutional-grade systems designed to last decades.',
    'about.focus': 'Global South Focus',
    'about.focus.desc': 'Headquartered in the heart of Asia, we design technologies specifically for the rising powers of the Global South, ensuring technological sovereignty and independence from legacy hegemonies.',
    'about.cyber': 'Cyber-Physical Reality',
    'about.cyber.desc': 'Our work transcends the screen. We integrate intelligence into the physical world — from smart city grids to defense systems and industrial automation.',
    'about.roadmap': 'Strategic Roadmap',
    'about.leadership': 'Leadership Council',
    'about.timeline.2023': 'Foundation',
    'about.timeline.2023.desc': 'Establishment of core sovereign AI protocols.',
    'about.timeline.2024': 'Expansion',
    'about.timeline.2024.desc': 'Deployment of first cyber-physical defense grid.',
    'about.timeline.2026': 'Autonomy',
    'about.timeline.2026.desc': 'Full-scale autonomous infrastructure management.',
    'about.timeline.2030': 'Singularity',
    'about.timeline.2030.desc': 'Integration of general purpose decision systems.',
    'about.vision': 'Vision Horizon',
    'philosophy.subtitle': 'Core Beliefs',
    'philosophy.title': 'TECHNOLOGY BEYOND CONTROL',
    'philosophy.quote': 'We do not seek to conquer nature, but to build a second nature that is just, resilient, and aware.',
    'philosophy.bounded': 'Intelligence Must Be Bounded',
    'philosophy.bounded.desc': 'Unchecked artificial intelligence poses existential risks. We believe in "Bounded Intelligence" — systems that are incredibly powerful within their specific domains but are architecturally constrained from exceeding their mandate. Control is not an afterthought; it is the foundation.',
    'philosophy.sovereignty': 'Sovereignty is Non-Negotiable',
    'philosophy.sovereignty.desc': 'Nations and institutions must own their technological stack. We do not rent intelligence; we build sovereign capabilities that ensure our partners are never beholden to external actors or black-box algorithms they cannot audit.',
    'philosophy.human': 'The Human Remains in the Loop',
    'philosophy.human.desc': 'Automation should elevate human decision-making, not replace it. Our systems are designed as "Decision Support Architectures" — providing leaders with perfect information and predictive clarity, while leaving the final moral authority to humans.',
    'solutions.title': 'STRATEGIC SOLUTIONS',
    'solutions.subtitle': 'Bespoke technological interventions for high-stakes environments.',
    'solutions.ai.title': 'Sovereign AI Infrastructure',
    'solutions.ai.desc': 'End-to-end deployment of private, air-gapped Large Language Models and neural networks for government and defense applications.',
    'solutions.ai.feat1': 'On-premise deployment',
    'solutions.ai.feat2': 'Custom model training',
    'solutions.ai.feat3': 'Data residency compliance',
    'solutions.gov.title': 'Predictive Governance Systems',
    'solutions.gov.desc': 'City-scale sensor fusion and analytics platforms that allow administrators to anticipate urban challenges before they occur.',
    'solutions.gov.feat1': 'Real-time telemetry',
    'solutions.gov.feat2': 'Crowd dynamics',
    'solutions.gov.feat3': 'Resource optimization',
    'solutions.defense.title': 'Critical Asset Defense',
    'solutions.defense.desc': 'Autonomous cybersecurity layers for power grids, water systems, and transportation networks.',
    'solutions.defense.feat1': 'Anomaly detection',
    'solutions.defense.feat2': 'Automated response',
    'solutions.defense.feat3': 'Threat simulation',
    'solutions.data.title': 'Strategic Data Vaults',
    'solutions.data.desc': 'Secure, immutable data storage architectures designed for long-term institutional memory and continuity.',
    'solutions.data.feat1': 'Quantum-resistant encryption',
    'solutions.data.feat2': 'Distributed ledgers',
    'solutions.data.feat3': 'Access control',
    'solutions.cta': 'Explore Capability',
    'tech.title': 'THE TECH STACK',
    'tech.subtitle': 'Our architecture is built on three immutable principles: resilience, sovereignty, and explainability.',
    'tech.layer1': 'LAYER 01',
    'tech.edge': 'Edge Computing Fabric',
    'tech.edge.desc': 'We move intelligence to the edge. By processing data locally on devices rather than centralized clouds, we reduce latency, enhance privacy, and ensure system resilience even when network connectivity is severed.',
    'tech.layer2': 'LAYER 02',
    'tech.neuro': 'Neuro-Symbolic AI',
    'tech.neuro.desc': 'Pure deep learning is a black box. We utilize neuro-symbolic architectures that combine the learning capabilities of neural networks with the logical reasoning of symbolic AI. This creates systems that are not just accurate, but explainable and auditable.',
    'tech.layer3': 'LAYER 03',
    'tech.hardware': 'Hardware-Software Fusion',
    'tech.hardware.desc': 'Software cannot exist in a vacuum. We design custom hardware specifications and sensor arrays to ensure our software has the precise physical interface it needs to operate effectively in the real world.',
    'ethics.title': 'ETHICS & GOVERNANCE',
    'ethics.quote': 'Power without control is not technology; it is chaos.',
    'ethics.alignment': 'The Alignment Protocol',
    'ethics.alignment.desc': "Every system we deploy undergoes a rigorous alignment protocol. We mathematically verify that the system's objective function cannot deviate from the intended human values.",
    'ethics.sovereignty': 'Data Sovereignty',
    'ethics.sovereignty.desc': 'We adhere to the strictest definitions of data sovereignty. Data generated within a jurisdiction stays within that jurisdiction. We build "clean rooms" for data processing.',
    'ethics.transparency': 'Algorithmic Transparency',
    'ethics.transparency.desc': 'For public sector applications, we provide "Glass Box" implementations where the decision-making logic of the AI is accessible to auditors. We reject "Black Box" governance.',
    'ethics.killswitch': 'Kill-Switch Architecture',
    'ethics.killswitch.desc': 'All autonomous systems include hardware-level kill switches that are physically separated from the main logic board, ensuring human override capability is never compromised.',
    'insights.title': 'INTELLIGENCE LOGS',
    'insights.all': 'ALL',
    'insights.intelligence': 'INTELLIGENCE',
    'insights.infrastructure': 'INFRASTRUCTURE',
    'insights.geopolitics': 'GEOPOLITICS',
    'contact.title': 'GET IN TOUCH',
    'contact.desc': 'We engage with sovereign entities, government bodies, and large-scale industrial partners across Asia and beyond.',
    'contact.entity': 'Legal Entity',
    'contact.registered': 'Registered Technology Institution',
    'contact.headquarters': 'Headquarters',
    'contact.email': 'Official Email',
    'contact.hub.title': 'Regional Hub',
    'contact.hub.desc': "Strategic position in Southeast Asia's technology corridor",
    'contact.enterprise.title': 'Enterprise Focus',
    'contact.enterprise.desc': 'Serving government and institutional partners',
    'contact.cta.title': 'Ready to Build the Future?',
    'contact.cta.desc': 'Reach out to discuss sovereign technology solutions for your institution.',
    'contact.cta.button': 'Send Email',
    'footer.desc': 'Designing future technology systems that are intelligent, autonomous, and ethical by design. Advanced, artificial, and computational — yet consciously bounded.',
    'footer.institution': 'Institution',
    'footer.domains': 'Domains',
    'footer.privacy': 'PRIVACY PROTOCOL',
    'footer.status': 'SYSTEM STATUS: NOMINAL'
  },
  id: {
    'nav.about': 'Tentang',
    'nav.philosophy': 'Filosofi',
    'nav.solutions': 'Solusi',
    'nav.technology': 'Teknologi',
    'nav.ethics': 'Etika',
    'nav.insights': 'NETKRIDA',
    'nav.contact': 'Kontak',
    'home.hero.title': 'TEKNOLOGI MELAMPAUI KENDALI.',
    'home.hero.subtitle': 'Kami merancang sistem teknologi masa depan yang cerdas, otonom, dan etis sejak awal.',
    'home.hero.cta1': 'Jelajahi Solusi',
    'home.hero.cta2': 'Hubungi Kami',
    'home.stats.projects': 'Tahun Pengalaman',
    'home.stats.partners': 'Mitra Global',
    'home.stats.sovereignty': 'Kedaulatan Data %',
    'home.stats.monitoring': 'Pemantauan 24 Jam',
    'home.manifesto.title': 'Manifesto Institusional',
    'home.manifesto.heading': 'Ini bukan perusahaan dari hari ini. Ini adalah perusahaan dari dekade berikutnya.',
    'home.manifesto.desc': 'NEW ASIA TECH mewakili pendekatan peradaban baru terhadap teknologi canggih. Kami beroperasi di persimpangan kecerdasan buatan, sistem cyber-fisik, dan infrastruktur masa depan. Teknologi kami canggih, artifisial, dan komputasional — namun secara sadar dibatasi.',
    'home.manifesto.cta': 'Baca Filosofi Kami',
    'home.domains.title': 'DOMAIN INTI',
    'home.domains.subtitle': 'KAPABILITAS STRATEGIS',
    'home.domains.ai.title': 'Kecerdasan Buatan',
    'home.domains.ai.desc': 'Sistem pendukung keputusan skala besar dan agen otonom untuk lingkungan kompleks.',
    'home.domains.cyber.title': 'Sistem Cyber-Fisik',
    'home.domains.cyber.desc': 'Menjembatani dunia digital dan fisik melalui IoT canggih dan infrastruktur kontrol.',
    'home.domains.defense.title': 'Pertahanan Berdaulat',
    'home.domains.defense.desc': 'Sistem keamanan human-in-the-loop yang dirancang untuk ketahanan nasional dan institusional.',
    'home.cta.title': 'MEMBANGUN ERA BERIKUTNYA',
    'home.cta.desc': 'Bergabunglah dengan garda depan teknologi berdaulat. Amankan infrastruktur Anda untuk abad mendatang.',
    'home.cta.button': 'Hubungi Kami',
    'about.title': 'PROFIL INSTITUSIONAL',
    'about.who': 'Siapa Kami',
    'about.who.desc': 'NEW ASIA TECH adalah institusi teknologi strategis yang fokus pada desain dan implementasi sistem intelijen berdaulat.',
    'about.paradigm': 'Paradigma Pasca-Startup',
    'about.paradigm.desc': 'Kami menolak ideologi "bergerak cepat dan merusak segalanya" dari awal abad ke-21. Di era kecerdasan buatan kritis dan infrastruktur otonom, stabilitas, presisi, dan tata kelola etis adalah yang utama. Kami tidak membangun MVP; kami membangun sistem tingkat institusional yang dirancang untuk bertahan puluhan tahun.',
    'about.focus': 'Fokus Global South',
    'about.focus.desc': 'Berkantor pusat di jantung Asia, kami merancang teknologi khusus untuk kekuatan bangkit Global South, memastikan kedaulatan teknologi dan kemandirian dari hegemoni lama.',
    'about.cyber': 'Realitas Cyber-Fisik',
    'about.cyber.desc': 'Pekerjaan kami melampaui layar. Kami mengintegrasikan kecerdasan ke dunia fisik — dari jaringan kota pintar hingga sistem pertahanan dan otomasi industri.',
    'about.roadmap': 'Peta Jalan Strategis',
    'about.leadership': 'Dewan Kepemimpinan',
    'about.timeline.2023': 'Fondasi',
    'about.timeline.2023.desc': 'Pembentukan protokol AI berdaulat inti.',
    'about.timeline.2024': 'Ekspansi',
    'about.timeline.2024.desc': 'Penerapan jaringan pertahanan cyber-fisik pertama.',
    'about.timeline.2026': 'Otonomi',
    'about.timeline.2026.desc': 'Manajemen infrastruktur otonom skala penuh.',
    'about.timeline.2030': 'Singularitas',
    'about.timeline.2030.desc': 'Integrasi sistem keputusan tujuan umum.',
    'about.vision': 'Visi Horizon',
    'philosophy.subtitle': 'Keyakinan Inti',
    'philosophy.title': 'TEKNOLOGI MELAMPAUI KENDALI',
    'philosophy.quote': 'Kami tidak berusaha menaklukkan alam, tetapi membangun alam kedua yang adil, tangguh, dan sadar.',
    'philosophy.bounded': 'Kecerdasan Harus Dibatasi',
    'philosophy.bounded.desc': 'Kecerdasan buatan yang tidak terkendali menimbulkan risiko eksistensial. Kami percaya pada "Kecerdasan Terbatas" — sistem yang sangat kuat dalam domain spesifik mereka tetapi secara arsitektural dibatasi agar tidak melampaui mandat mereka. Kontrol bukan sesuatu yang ditambahkan kemudian; itu adalah fondasi.',
    'philosophy.sovereignty': 'Kedaulatan Tidak Dapat Ditawar',
    'philosophy.sovereignty.desc': 'Negara dan institusi harus memiliki tumpukan teknologi mereka. Kami tidak menyewakan kecerdasan; kami membangun kemampuan berdaulat yang memastikan mitra kami tidak pernah bergantung pada aktor eksternal atau algoritma kotak hitam yang tidak dapat mereka audit.',
    'philosophy.human': 'Manusia Tetap dalam Lingkaran',
    'philosophy.human.desc': 'Otomasi harus meningkatkan pengambilan keputusan manusia, bukan menggantikannya. Sistem kami dirancang sebagai "Arsitektur Dukungan Keputusan" — memberikan pemimpin informasi sempurna dan kejelasan prediktif, sambil menyerahkan otoritas moral akhir kepada manusia.',
    'solutions.title': 'SOLUSI STRATEGIS',
    'solutions.subtitle': 'Intervensi teknologi khusus untuk lingkungan berisiko tinggi.',
    'solutions.ai.title': 'Infrastruktur AI Berdaulat',
    'solutions.ai.desc': 'Penerapan end-to-end Model Bahasa Besar dan jaringan saraf pribadi yang terisolasi untuk aplikasi pemerintah dan pertahanan.',
    'solutions.ai.feat1': 'Penerapan on-premise',
    'solutions.ai.feat2': 'Pelatihan model khusus',
    'solutions.ai.feat3': 'Kepatuhan residensi data',
    'solutions.gov.title': 'Sistem Tata Kelola Prediktif',
    'solutions.gov.desc': 'Platform fusi sensor dan analitik skala kota yang memungkinkan administrator mengantisipasi tantangan urban sebelum terjadi.',
    'solutions.gov.feat1': 'Telemetri real-time',
    'solutions.gov.feat2': 'Dinamika kerumunan',
    'solutions.gov.feat3': 'Optimasi sumber daya',
    'solutions.defense.title': 'Pertahanan Aset Kritis',
    'solutions.defense.desc': 'Lapisan keamanan siber otonom untuk jaringan listrik, sistem air, dan jaringan transportasi.',
    'solutions.defense.feat1': 'Deteksi anomali',
    'solutions.defense.feat2': 'Respons otomatis',
    'solutions.defense.feat3': 'Simulasi ancaman',
    'solutions.data.title': 'Brankas Data Strategis',
    'solutions.data.desc': 'Arsitektur penyimpanan data yang aman dan tidak dapat diubah yang dirancang untuk memori institusional jangka panjang dan kontinuitas.',
    'solutions.data.feat1': 'Enkripsi tahan kuantum',
    'solutions.data.feat2': 'Buku besar terdistribusi',
    'solutions.data.feat3': 'Kontrol akses',
    'solutions.cta': 'Jelajahi Kapabilitas',
    'tech.title': 'TUMPUKAN TEKNOLOGI',
    'tech.subtitle': 'Arsitektur kami dibangun di atas tiga prinsip yang tidak dapat diubah: ketahanan, kedaulatan, dan kemampuan dijelaskan.',
    'tech.layer1': 'LAPISAN 01',
    'tech.edge': 'Fabric Komputasi Edge',
    'tech.edge.desc': 'Kami memindahkan kecerdasan ke edge. Dengan memproses data secara lokal di perangkat daripada cloud terpusat, kami mengurangi latensi, meningkatkan privasi, dan memastikan ketahanan sistem bahkan ketika konektivitas jaringan terputus.',
    'tech.layer2': 'LAPISAN 02',
    'tech.neuro': 'AI Neuro-Simbolik',
    'tech.neuro.desc': 'Deep learning murni adalah kotak hitam. Kami menggunakan arsitektur neuro-simbolik yang menggabungkan kemampuan pembelajaran jaringan saraf dengan penalaran logis AI simbolik. Ini menciptakan sistem yang tidak hanya akurat, tetapi dapat dijelaskan dan diaudit.',
    'tech.layer3': 'LAPISAN 03',
    'tech.hardware': 'Fusi Hardware-Software',
    'tech.hardware.desc': 'Software tidak dapat eksis dalam ruang hampa. Kami merancang spesifikasi hardware khusus dan array sensor untuk memastikan software kami memiliki antarmuka fisik yang tepat yang dibutuhkan untuk beroperasi secara efektif di dunia nyata.',
    'ethics.title': 'ETIKA & TATA KELOLA',
    'ethics.quote': 'Kekuatan tanpa kontrol bukanlah teknologi; itu adalah kekacauan.',
    'ethics.alignment': 'Protokol Penyelarasan',
    'ethics.alignment.desc': 'Setiap sistem yang kami terapkan menjalani protokol penyelarasan yang ketat. Kami memverifikasi secara matematis bahwa fungsi objektif sistem tidak dapat menyimpang dari nilai-nilai manusia yang dimaksudkan.',
    'ethics.sovereignty': 'Kedaulatan Data',
    'ethics.sovereignty.desc': 'Kami mematuhi definisi kedaulatan data yang paling ketat. Data yang dihasilkan dalam yurisdiksi tetap dalam yurisdiksi itu. Kami membangun "ruang bersih" untuk pemrosesan data.',
    'ethics.transparency': 'Transparansi Algoritmik',
    'ethics.transparency.desc': 'Untuk aplikasi sektor publik, kami menyediakan implementasi "Kotak Kaca" di mana logika pengambilan keputusan AI dapat diakses oleh auditor. Kami menolak tata kelola "Kotak Hitam".',
    'ethics.killswitch': 'Arsitektur Kill-Switch',
    'ethics.killswitch.desc': 'Semua sistem otonom mencakup kill switch tingkat hardware yang secara fisik terpisah dari papan logika utama, memastikan kemampuan override manusia tidak pernah dikompromikan.',
    'insights.title': 'LOG INTELIJEN',
    'insights.all': 'SEMUA',
    'insights.intelligence': 'INTELIJEN',
    'insights.infrastructure': 'INFRASTRUKTUR',
    'insights.geopolitics': 'GEOPOLITIK',
    'contact.title': 'HUBUNGI KAMI',
    'contact.desc': 'Kami bekerja sama dengan entitas berdaulat, badan pemerintah, dan mitra industri skala besar di seluruh Asia dan sekitarnya.',
    'contact.entity': 'Badan Hukum',
    'contact.registered': 'Institusi Teknologi Terdaftar',
    'contact.headquarters': 'Kantor Pusat',
    'contact.email': 'Email Resmi',
    'contact.hub.title': 'Hub Regional',
    'contact.hub.desc': 'Posisi strategis di koridor teknologi Asia Tenggara',
    'contact.enterprise.title': 'Fokus Enterprise',
    'contact.enterprise.desc': 'Melayani mitra pemerintah dan institusional',
    'contact.cta.title': 'Siap Membangun Masa Depan?',
    'contact.cta.desc': 'Hubungi kami untuk mendiskusikan solusi teknologi berdaulat untuk institusi Anda.',
    'contact.cta.button': 'Kirim Email',
    'footer.desc': 'Merancang sistem teknologi masa depan yang cerdas, otonom, dan etis sejak awal. Canggih, artifisial, dan komputasional — namun secara sadar dibatasi.',
    'footer.institution': 'Institusi',
    'footer.domains': 'Domain',
    'footer.privacy': 'PROTOKOL PRIVASI',
    'footer.status': 'STATUS SISTEM: NOMINAL'
  }
};
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('en');
  const t = (key: string): string => translations[language][key] || key;
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t
  }} data-id="element-184">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
}